import { RightOutlined, UserAddOutlined } from '@ant-design/icons';
import { Avatar, Tag } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import {
  TIENDA_PERFECTA_DOCUMENT_SLOTS,
  TiendaPerfectaCrewMember,
} from 'models/TiendaPerfecta';

import { getCrewAvatarColor, getInitials } from '../types';

const I18N_BASE = 'scenes.vans.components.crew_slot';

interface CrewSlotProps {
  member: TiendaPerfectaCrewMember | null;
  /** Qué puesto ocupa este slot, para el texto cuando está vacío. */
  role: 'conductor' | 'peoneta';
  /** Se muestra solo en el conductor: explica por qué la camioneta sale sola. */
  emptyHint?: string;
  onClick: (operatorId: string) => void;
}

function CrewSlot({ member, role, emptyHint, onClick }: CrewSlotProps) {
  const { t } = useTranslation();

  if (!member) {
    return (
      <div className="vans-crew-slot vans-crew-slot--empty">
        <Avatar
          size={36}
          icon={<UserAddOutlined />}
          className="vans-crew-slot__ghost"
        />
        <div className="vans-crew-slot__info">
          <div className="vans-crew-slot__empty-title">
            {t(`${I18N_BASE}.empty.${role}`)}
          </div>
          {emptyHint && (
            <div className="vans-crew-slot__empty-hint">{emptyHint}</div>
          )}
        </div>
      </div>
    );
  }

  const total = TIENDA_PERFECTA_DOCUMENT_SLOTS.length;
  const loaded = TIENDA_PERFECTA_DOCUMENT_SLOTS.filter(
    (slot) => member.documents?.[slot]
  ).length;

  const expiry = member.licenseExpiry ? dayjs(member.licenseExpiry) : null;
  const expired = expiry ? expiry.isBefore(dayjs(), 'day') : false;

  const documento = [member.documentType?.code, member.documentNumber]
    .filter(Boolean)
    .join(' ');
  const telefono = [member.dialCode, member.phoneNumber]
    .filter(Boolean)
    .join(' ');
  const meta = [documento, telefono].filter(Boolean).join(' · ');

  return (
    <div
      className="vans-crew-slot vans-crew-slot--clickable"
      onClick={() => onClick(member.operatorId)}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          onClick(member.operatorId);
        }
      }}
    >
      <Avatar
        size={36}
        style={{ backgroundColor: getCrewAvatarColor(member.operatorId) }}
      >
        {getInitials(member.fullName)}
      </Avatar>

      <div className="vans-crew-slot__info">
        <div className="vans-crew-slot__name">
          <span className="vans-crew-slot__name-text">{member.fullName}</span>
          <Tag
            color={
              member.fieldRole?.value === 'conductor' ? 'purple' : undefined
            }
          >
            {member.fieldRole?.label}
          </Tag>
        </div>

        {/* El teléfono puede venir vacío: se arma la línea con lo que exista
            para no dejar un separador colgando. */}
        <div className="vans-crew-slot__meta">{meta}</div>

        <div className="vans-crew-slot__docs">
          <Tag color={loaded === total ? 'green' : 'gold'}>
            {t(`${I18N_BASE}.documents`, { loaded, total })}
          </Tag>
          {expiry && (
            <Tag color={expired ? 'red' : undefined}>
              {expired
                ? t(`${I18N_BASE}.license_expired`, {
                    date: expiry.format('DD/MM/YYYY'),
                  })
                : t(`${I18N_BASE}.license_expires`, {
                    date: expiry.format('DD/MM/YYYY'),
                  })}
            </Tag>
          )}
        </div>
      </div>

      <RightOutlined className="vans-crew-slot__chevron" />
    </div>
  );
}

export default CrewSlot;
